<script>
    import { allProjectFields } from "../../Participaid.svelte";
    import ProjectFieldLib from "../../models/participaid/ProjectField.js";

    import Dialog from "../Dialog.svelte";
    import Emoji from "../Emoji.svelte";
    import LoadingSpinner from "../LoadingSpinner.svelte";
    import LoadingModal from "../LoadingModal.svelte";
    import ProjectFieldDefinition from "../participaid/ProjectFieldDefinition.svelte";
    import Field from "../form/Field.svelte";
    import TextField from "../form/TextField.svelte";
    import FormulaOperationDropdown from "../form/FormulaOperationDropdown.svelte";
    import FormulaTypeDropdown from "../form/FormulaTypeDropdown.svelte";
    import FormulaInputTypeDropdown from "../form/FormulaInputTypeDropdown.svelte";
    import FormulaTypeWithParametersField from "../form/FormulaTypeWithParametersField.svelte";
    import NumberField from "../form/NumberField.svelte";
    import DialogScreen from "../DialogScreen.svelte";
    import DialogScreenContainer from "../DialogScreenContainer.svelte";

    import Paper, {Title, Subtitle, Content} from '@smui/paper';
    import Button, {Label} from '@smui/button';
    import FaIcon from 'fa-svelte'
    import DataTable, {Head, Body, Row, Cell} from '@smui/data-table';
    import Select, {Option} from '@smui/select';
    import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
    import { createEventDispatcher } from 'svelte';
    import { fade } from 'svelte/transition';

    // TODO: wrap into some kind of standardized eventbus
    const dispatch = createEventDispatcher();

    export let show;
    export let model;
    export let editable;

    // TODO: disallow title with __ (metafield)

    //
    // break down fields from model in separate variables
    //

    // model
    let name = (model && model.name) || "";
    let description = (model && model.description) || "";
    let conditionDescription = (model && model.conditionDescription) || "";
    let multiplier = (model && model.multiplier) || 1.0;
    let allowedAttachments = [];
    let parameters = (
           model
        && model.parameters
        && model.parameters.arguments.filter(x => x).length
        && model.parameters.arguments
    ) || [];

    // formula
    let formulas = {};
    let formulaName = model && model.formulaType.name;
    let formulaOperation = model && model.formulaType.operation;
    let formulaTyping = model && (model.formulaType.type || "NONE");
    let formulaInputType = model && (ProjectFieldLib.getFieldInputType(model) || "").toUpperCase();

    // load predefined formula types and set to var
    ProjectFieldLib.getFormulaTypes().then(f => {
        // TODO: remove need to hardcode COMMISSION_COLLABORATOR id here (comes from GQL migration seed)
        formulas = {...f, ...{COMMISSION_COLLABORATOR: {id: "734c0e2a-7ba0-4c79-af03-245bda1aad36"}}};
        console.log(`EditProjectFieldDialog received preset formula types:`);
        console.log(f);
    });

    // the fields "name" and "unit" here are ignored and hardcoded based on the formulatype
    let formulaVariable = parameters && parameters.filter(x => x).length && parameters[0].value;
    let commissionPercentage = formulaVariable || 0;
    let hoursPerYear = formulaVariable || 1600;

    // if we have chosen a formula type that contains a commission variable,
    // define the variable to be passed in the field definition
    // TODO: refactor this into a lib/model
    $: isCommissionContrInput = formulaTyping && formulaTyping == "COMMISSION_COLLABORATOR";
    $: isCommissionGenInput = formulaTyping && formulaTyping == "COMMISSION_GENERAL";
    $: isCommissionInput = formulaTyping && isCommissionGenInput || isCommissionContrInput;
    $: isTimeInput = ProjectFieldLib.isTimeType(formulaInputType);
    $: isAnnualSalaryInput = formulaTyping && formulaTyping == "YEAR_SALARY";

    // this should be the ID of the preset formulatype.
    $: formulasLoaded = formulas && Object.keys(formulas).length;
    $: formulaTypeId = formulasLoaded && formulaTyping && formulas[formulaTyping] && formulas[formulaTyping].id;

    // whether we chose a formula type that accepts a variable
    $: isVariableInput = isCommissionGenInput || isAnnualSalaryInput;

    // if we chose a formula type that supports a commission variable,
    // this would be the struct that we pass in the dispatched model
    $: commissionVariable = !isCommissionGenInput ? undefined : {
        name: "CommissionPercentage", // see GQL ContributionCalculator
        unit: "percent",
        value: "" + commissionPercentage
    };

    // if we chose a formula type that supports a hours-in-year variable,
    // this would be the struct that we pass in the dispatched model
    $: hoursInYearVariable = (!isAnnualSalaryInput) ? undefined : {
        name: "NumberOfHours", // see GQL ContributionCalculator
        unit: "Hour",
        value: "" + hoursPerYear
    };

    $: calculatedVariable = (
        (isCommissionInput)
            ? commissionVariable
            : (
                (isAnnualSalaryInput)
                    ? hoursInYearVariable
                    : undefined
            )
    );

    $: calculatedParameterTpl = (!isVariableInput) ? undefined : {
        arguments: [
            { name: "CommissionPercentage", unit: "percent" }
        ]
    };

    $: formulaType = {
        id: formulaTypeId,
        name: formulaName,
        type: formulaTyping || "NONE",
        parameterTemplate: calculatedParameterTpl,
        operation: formulaOperation,
        variable: calculatedVariable
    };

    $: dispatchOp = !!model ? "update" : "new";

    const close = () => dispatch("close");

    let submitRequest;
    let currentIndex = 0;

    function submit() {
        if (editable) {
            dispatch(dispatchOp, {
                name,
                description,
                conditionDescription,
                multiplier,
                allowedAttachments,
                parameters: (!isVariableInput) ? undefined : {arguments: [calculatedVariable]},
                formulaType
            });
        }

        else {
            close();
        }

        show = false;
    }

    $: invalid = (
           (currentIndex == 0 && (!name || !formulaInputType))
        || (currentIndex == 1 && (false))
        || (currentIndex == 2 && !conditionDescription)
    );

    $: {
        console.log(`current formula typing: ${formulaInputType}`)
    }
</script>

<Dialog {show} class="project-field-dialog" request={submitRequest}>
    <h1>Edit Partipie entry field <Emoji name="receipt"/></h1>
    <DialogScreenContainer bind:currentIndex lastIndex={3} nextDisabled={invalid} on:end={submit} on:cancel={close} on:delete cancelButton {editable}>
        <span slot="screen-1">
            <DialogScreen>
                <p>Adjust the field type that is used to classify contributions made by participants</p>

                <Field class="name-cell">
                    <TextField bind:value={name} help="Title of this input field" disabled={!editable}/>
                </Field>

                <Field class="field-type">
                    <FormulaInputTypeDropdown bind:selectedId={formulaInputType} disabled={!editable} />
                </Field>

                <Field class="attachments-cell">

                </Field>
            </DialogScreen>
        </span>

        <span slot="screen-2">
            <DialogScreen>
                <p>Describe the kind of asset that is brought in when using this field</p>
                <Field class="description-cell">
                    <TextField
                            class="project-field-description"
                            style="height: 200px; width: 100%"
                            textarea
                            bind:value={description}
                            label="Field description"
                            help="Type description of the value that the contributor added to the company. For example, invoice, loan, time, assets, royalties, ideas, connections..."
                            placeholder="Invoice... service... supplies..."
                            disabled={!editable}
                    />
                </Field>
            </DialogScreen>
        </span>

        <span slot="screen-3">
            <DialogScreen>
                <p>Describe when you will accept, and when you will reject entries of this type.</p>
                <Field class="description-condition-cell">
                    <TextField
                            class="project-field-description"
                            style="height: 200px; width: 100%"
                            textarea
                            bind:value={conditionDescription}
                            label="Acceptance requirements"
                            help="A description of the rules and conditions the contribution has to meet to be accepted by the Project Owner (you). For example, 'every contribution needs an invoice in PDF form'... 'invoices may only be accepted on Tuesdays'... 'Couches may only be valued under 300 Euros'. Etc..."
                            placeholder="Needs an invoice... a proof... agreement in advance..."
                            disabled={!editable}
                    />
                </Field>
            </DialogScreen>
        </span>

        <span slot="screen-4">
            <DialogScreen>
                <p>Configure the formula used to calculate the value of the contribution</p>
<!--                <Field class="formula-name">-->
<!--                    <TextField-->
<!--                            class="project-field-formula-name"-->
<!--                            style="height: 100%; width: 100%"-->
<!--                            bind:value={formulaName}-->
<!--                            label="Name"-->
<!--                            help="The name for this valuation calculation formula"-->
<!--                            placeholder="Name"-->
<!--                    />-->
<!--                </Field>-->

                <FormulaTypeWithParametersField
                        bind:formula={formulaTyping}
                        isTime={isTimeInput}
                        bind:hoursPerYear
                        bind:commissionPercentage
                        disabled={!editable}
                />

                <Field class="multiplier-cell">
                    <NumberField bind:value={multiplier}
                                 step="0.01"
                                 min="0"
                                 label="Multiplier"
                                 disabled={!editable}
                                 help="The value multiplier that is used to account for the 'thing' being registered. Since coworkers may not yet be paid, they deserve a premium for doing work that could actually earn them a salary somewhere else."/>
                </Field>
            </DialogScreen>
        </span>
    </DialogScreenContainer>
</Dialog>

<style>
    p {
        text-align: center;
    }

    /*:global(.project-field-dialog span[slot="screen-1"] .form-field > .inner) {
        display: flex;
        flex-direction: row;
    }

    :global(.project-field-dialog span[slot="screen-1"] .dialog-screen) {
        max-width: 100%;
        width: 100%;
    }

    :global(.project-field-dialog span[slot="screen-1"] .mdc-text-field-helper-text, .project-field-dialog span[slot="screen-1"] .mdc-select-helper-text) {
        width: 300px;
    }

    :global(.project-field-dialog span[slot="screen-1"] .form-field > .inner > .mdc-text-field, .project-field-dialog span[slot="screen-1"] .form-field > .inner > .mdc-select) {
        flex: 1;
    }*/
</style>